import React from 'react'
import * as Yup from 'yup'
import { CREDIT_PAYEE_LABELS, OptiRateDetails, ServiceAgreement } from '../../../../apis/types'
import Badge from '../../../../components/Badge'
import { formatUTCDate } from '../../../../util/string'
import PortaledTooltip from './PortaledTooltip'

export interface ServiceAgreementWithAccountNumber extends ServiceAgreement {
  tenantAccountNumber: string
}

export function formatVnemPendingChangeDescription(
  change: NonNullable<ServiceAgreement['vnemPendingChange']>
): string {
  const parts: string[] = []
  if (change.percentage != null) {
    parts.push(`${change.percentage.from}% → ${change.percentage.to}%`)
  }
  if (change.tariffSchedule != null) {
    const from = change.tariffSchedule.from ?? '—'
    const to = change.tariffSchedule.to ?? '—'
    parts.push(`${from} → ${to}`)
  }
  if (parts.length === 0) return 'Pending allocation change at utility'
  return `Pending allocation change: ${parts.join(', ')}`
}

/** Secondary cue when SA is still on-table but % / tariff changed on an unactioned submission. */
export const VnemPendingChangeBadge: React.FC<{
  change: NonNullable<ServiceAgreement['vnemPendingChange']>
}> = ({ change }) => (
  <PortaledTooltip
    content={
      <div className="max-w-xs space-y-1 text-left">
        <div className="text-xs font-medium text-white">Pending VNEM Change</div>
        <p className="mb-0 text-xs font-normal leading-snug text-slate-200">
          {formatVnemPendingChangeDescription(change)}
        </p>
      </div>
    }
    className="inline-flex w-fit shrink-0"
  >
    <Badge status="PENDING_CHANGE" variant="soft" />
  </PortaledTooltip>
)

export const badgeClasses =
  'inline-flex items-center px-1.5 py-0.5 ring-1 ring-inset rounded text-xs font-medium'

/** Allow empty or a real calendar date as YYYY-MM-DD. */
export const isoDateSchema = Yup.string().test('iso-date', 'Must be YYYY-MM-DD', (value) => {
  if (value == null || value === '') return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return (
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
  )
})

export const toIsoDateValue = (value: string | Date | null | undefined): string | null => {
  if (value == null || value === '') return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  return new Date(value).toISOString().split('T')[0] ?? null
}

export function formatServiceAddress(record: ServiceAgreementWithAccountNumber): string {
  const sAdd = record.serviceAddress
  return [sAdd.street1, sAdd.street2, `${sAdd.city}, ${sAdd.state} ${sAdd.zip}`].filter(Boolean).join(', ')
}

export function formatPeriodCompact(record: ServiceAgreementWithAccountNumber): string {
  const start = record.startDate != null ? formatUTCDate(record.startDate) : null
  const end = record.endDate != null ? formatUTCDate(record.endDate) : null
  if (start == null && end == null) return '—'
  return `${start ?? '—'} – ${end ?? '—'}`
}

export function optiRateTooltip(details: OptiRateDetails): string {
  const lines = [
    details.status,
    details.originalSAId != null ? `Original SA: ${details.originalSAId}` : null,
    details.optiRateSAId != null ? `OptiRate SA: ${details.optiRateSAId}` : null,
    `${formatOptiRateTariffChange(details.originalTariff, details.targetTariff)}`,
    details.creditPayee != null ? `Credit Payee: ${CREDIT_PAYEE_LABELS[details.creditPayee]}` : null,
    `Requested ${formatUTCDate(new Date(details.requestDate))} by ${details.requestedBy}`,
    details.effectiveCycleStartDate
      ? `Cycle start ${formatUTCDate(new Date(details.effectiveCycleStartDate))}`
      : null,
  ]
  return lines.filter(Boolean).join('\n')
}

function formatOptiRateTariffChange(
  originalTariff: string,
  targetTariff: string,
  label: 'Optimized' | 'Reoptimization' = 'Optimized'
): string {
  return `${label} from ${originalTariff} to ${targetTariff}`
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {children}
    </div>
  )
}

function OptiRateAuditFields({
  details,
  label = 'Optimized',
  creditPayeeSlot,
}: {
  details: OptiRateDetails
  label?: 'Optimized' | 'Reoptimization'
  creditPayeeSlot?: React.ReactNode
}) {
  return (
    <div className="mt-1 space-y-0.5 text-sm text-slate-600 dark:text-slate-300">
      <div>{formatOptiRateTariffChange(details.originalTariff, details.targetTariff, label)}</div>
      {details.originalSAId != null && <div>Original SA: {details.originalSAId}</div>}
      {details.optiRateSAId != null && <div>OptiRate SA: {details.optiRateSAId}</div>}
      {creditPayeeSlot ??
        (details.creditPayee != null && (
          <div>Credit Payee: {CREDIT_PAYEE_LABELS[details.creditPayee]}</div>
        ))}
      <div>
        Requested {formatUTCDate(new Date(details.requestDate))} by {details.requestedBy}
      </div>
      {details.effectiveCycleStartDate != null && (
        <div>Cycle start {formatUTCDate(new Date(details.effectiveCycleStartDate))}</div>
      )}
    </div>
  )
}

export function OptiRateStatus({
  record,
  allRows,
  compact = false,
  detailed = false,
  creditPayeeSlot,
}: {
  record: ServiceAgreementWithAccountNumber
  allRows: ServiceAgreementWithAccountNumber[]
  /** When true, show badge only (table overview). */
  compact?: boolean
  /** When true, show full audit fields (detail view). */
  detailed?: boolean
  creditPayeeSlot?: React.ReactNode
}) {
  const details = record.optiRateDetails

  if (details?.status === 'ENROLLED') {
    const pendingReoptimization = allRows.find(
      (sa) =>
        sa.optiRateDetails?.status === 'PENDING' &&
        sa.optiRateDetails?.originalSAId === details.originalSAId
    )
    if (compact) {
      return (
        <span title={optiRateTooltip(details)}>
          <span
            className={`${badgeClasses} bg-emerald-100 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/30`}
          >
            ENROLLED
          </span>
        </span>
      )
    }
    return (
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={`${badgeClasses} bg-emerald-100 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/30`}
          >
            ENROLLED
          </span>
          {!detailed && (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {formatOptiRateTariffChange(details.originalTariff, details.targetTariff)}
            </span>
          )}
        </div>
        {detailed && <OptiRateAuditFields details={details} />}
        {pendingReoptimization?.optiRateDetails && (
          <div className="text-sm font-medium text-amber-700 dark:text-amber-400">
            Reoptimization pending from {pendingReoptimization.optiRateDetails.originalTariff} to{' '}
            {pendingReoptimization.optiRateDetails.targetTariff}
          </div>
        )}
      </div>
    )
  }

  if (details?.status === 'PENDING') {
    const isReoptimization = allRows.find(
      (sa) =>
        sa.optiRateDetails?.status === 'ENROLLED' &&
        sa.optiRateDetails?.originalSAId === details.originalSAId
    )
    if (compact) {
      return (
        <span title={optiRateTooltip(details)}>
          <span
            className={`${badgeClasses} bg-amber-100 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/30`}
          >
            PENDING
          </span>
        </span>
      )
    }
    return (
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={`${badgeClasses} bg-amber-100 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/30`}
          >
            PENDING
          </span>
          {!detailed && (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {formatOptiRateTariffChange(
                details.originalTariff,
                details.targetTariff,
                isReoptimization ? 'Reoptimization' : 'Optimized'
              )}
            </span>
          )}
        </div>
        {detailed && (
          <OptiRateAuditFields
            details={details}
            label={isReoptimization ? 'Reoptimization' : 'Optimized'}
            creditPayeeSlot={creditPayeeSlot}
          />
        )}
      </div>
    )
  }

  if (details?.status === 'ARCHIVED') {
    if (compact) {
      return (
        <span title={optiRateTooltip(details)}>
          <span
            className={`${badgeClasses} bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-400/10 dark:text-slate-300 dark:ring-slate-400/30`}
          >
            ARCHIVED
          </span>
        </span>
      )
    }
    return (
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={`${badgeClasses} bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-400/10 dark:text-slate-300 dark:ring-slate-400/30`}
          >
            ARCHIVED
          </span>
          {!detailed && (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {formatOptiRateTariffChange(details.originalTariff, details.targetTariff)}
            </span>
          )}
        </div>
        {detailed && <OptiRateAuditFields details={details} />}
      </div>
    )
  }

  if (compact) {
    return <Badge status="NOT_ENROLLED" variant="soft" />
  }

  return (
    <div className="space-y-0.5">
      <Badge status="NOT_ENROLLED" variant="soft" />
      {details?.status === 'NOT_ENROLLED' && details.optiRateSAId != null && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {detailed ? `New SA on OptiRate: ${details.optiRateSAId}` : `→ SA ${details.optiRateSAId}`}
        </div>
      )}
    </div>
  )
}

export function LastBilled({ record }: { record: ServiceAgreementWithAccountNumber }) {
  if (!record.lastBilledDate) return <span className="text-slate-500 dark:text-slate-400">—</span>
  const diffDays = Math.floor(
    (Date.now() - new Date(record.lastBilledDate).getTime()) / (1000 * 60 * 60 * 24)
  )
  let badge: React.ReactNode = null
  if (diffDays >= 90) {
    badge = (
      <span
        className={`${badgeClasses} bg-red-100 text-red-800 ring-red-600/20 dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/50`}
      >
        90+d
      </span>
    )
  } else if (diffDays >= 60) {
    badge = (
      <span
        className={`${badgeClasses} bg-orange-100 text-orange-800 ring-orange-600/40 dark:bg-orange-400/10 dark:text-orange-400 dark:ring-orange-400/20`}
      >
        60+d
      </span>
    )
  } else if (diffDays >= 30) {
    badge = (
      <span
        className={`${badgeClasses} bg-yellow-100 text-yellow-800 ring-yellow-600/30 dark:bg-yellow-400/10 dark:text-yellow-400 dark:ring-yellow-400/30`}
      >
        30+d
      </span>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-sm">{formatUTCDate(record.lastBilledDate)}</span>
      {badge}
    </div>
  )
}
